/**
 * Partner Attribution Service
 *
 * Reads partner referral codes from URL parameters.
 * No cookies — URL params survive OAuth redirect (via Supabase wildcard config).
 * Email signup stores attribution in user_metadata (cross-device).
 *
 * Flow:
 * 1. User lands on site with ?ref=PARTNER_CODE
 * 2. OAuth: GoogleSignInButton passes full URL as redirectTo → params survive redirect
 *    → SIGNED_IN fires → AuthContext reads URL params → calls POST /record-referral
 * 3. Email: signup() stores partner_code in user_metadata
 *    → User confirms → SIGNED_IN fires → AuthContext reads user_metadata → calls POST /record-referral
 * 4. Manual: User enters code during onboarding → calls POST /record-referral directly
 */

export interface PartnerAttribution {
  partner_code: string;
  attribution_source: 'url' | 'manual_code';
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  landing_page?: string;
  referrer_url?: string;
}

/**
 * Get partner attribution from current URL parameters.
 * Returns null if no partner code is present.
 *
 * Looks for: ?ref=CODE, ?partner=CODE, or ?affiliate=CODE
 */
export function getPartnerAttributionFromUrl(): PartnerAttribution | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);

  // Check for partner code in various parameter names
  const partnerCode = params.get('ref') || params.get('partner') || params.get('affiliate');

  if (!partnerCode) {
    return null;
  }

  return {
    partner_code: partnerCode.toUpperCase().trim(),
    attribution_source: 'url',
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    landing_page: window.location.pathname,
    referrer_url: document.referrer || undefined,
  };
}

/**
 * Create a manual partner attribution object.
 * Used when user enters a partner code during onboarding.
 */
export function createManualAttribution(code: string): PartnerAttribution {
  return {
    partner_code: code.toUpperCase().trim(),
    attribution_source: 'manual_code',
    landing_page: typeof window !== 'undefined' ? window.location.pathname : undefined,
  };
}

/**
 * Remove partner-related parameters from URL without page reload.
 * Call this after attribution has been captured/stored.
 */
export function cleanupAttributionParams(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const paramsToRemove = ['ref', 'partner', 'affiliate'];

  let changed = false;
  for (const param of paramsToRemove) {
    if (params.has(param)) {
      params.delete(param);
      changed = true;
    }
  }

  if (changed) {
    const newSearch = params.toString();
    const newUrl = newSearch
      ? `${window.location.pathname}?${newSearch}${window.location.hash}`
      : `${window.location.pathname}${window.location.hash}`;

    window.history.replaceState({}, '', newUrl);
  }
}

/** A conference sign-up token from the URL, which survives an OAuth redirect. */
export function getSignupTokenFromUrl(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return new URLSearchParams(window.location.search).get('signup_token') || null;
}

/** Drop the sign-up token from the address bar once it is stored on the account. */
export function cleanupSignupTokenParam(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  if (!params.has('signup_token')) {
    return;
  }
  params.delete('signup_token');
  const search = params.toString();
  const newUrl = search
    ? `${window.location.pathname}?${search}${window.location.hash}`
    : `${window.location.pathname}${window.location.hash}`;
  window.history.replaceState({}, '', newUrl);
}

/**
 * Check if URL has partner attribution params.
 * Use this for quick checks without making API call.
 */
export function hasAttributionParams(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  return !!(params.get('ref') || params.get('partner') || params.get('affiliate'));
}
